import { Inject, Injectable } from '@angular/core';
import * as _ from 'lodash';
import { ConfigConstants } from '../../config/config-constants';
import { AppVersion, AppVersionInstanceToken } from '../../interfaces/app-version.interface';
import { ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { EnvironmentInstanceToken, PaceEnvironment } from '../../interfaces/environment.interface';

export const AppId = {
    dev58: 'com.siemens.medical.solutions.usa.ngmDev58',
    qua58: 'com.siemens.medical.solutions.usa.ngmQuality58',
    qua58_2: 'com.siemens.medical.solutions.usa.ngmQuality58-2',
    t58: 'com.siemens.medical.solutions.usa.ngmTraining58',
    sandboxp58: 'com.siemens.medical.solutions.usa.ngmSandboxE58',
    prod58: 'com.siemens.medical.solutions.usa.ngmProd58',
    devAa: 'com.siemens.medical.solutions.usa.ngmDevAa',
    devAa2: 'com.siemens.medical.solutions.usa.ngmDevAa2',
    quaAa: 'com.siemens.medical.solutions.usa.ngmQualityAa',
    quaAa2: 'com.siemens.medical.solutions.usa.ngmQualityAa2',
    prodAa: 'com.siemens.medical.solutions.usa.ngmProdAa',
    prodAa2: 'com.siemens.medical.solutions.usa.ngmProdAa2',
    devCn: 'com.siemens.medical.solutions.usa.ngmDevCn',
    devCn2: 'com.siemens.medical.solutions.usa.ngmDevCn2',
    quaCn: 'com.siemens.medical.solutions.usa.ngmQualityCn',
    quaCn2: 'com.siemens.medical.solutions.usa.ngmQualityCn2',
    prodCn: 'com.siemens.medical.solutions.usa.ngmProdCn',
    prodCn2: 'com.siemens.medical.solutions.usa.ngmProdCn2',
    paceonline: 'com.siemens.medical.solutions.usa.paceonline',
    paceonline58: 'com.siemens.medical.solutions.usa.paceonline',
    paceonlineAa: 'com.siemens.medical.solutions.usa.paceonlineAa',
    paceFormsFI: 'com.siemens.medical.solutions.usa.paceFormsFI',
    paceFormsForAuthors: 'com.siemens.medical.solutions.usa.paceFormsForAuthors',
    dev40win: 'com.siemens.medical.solutions.usa.ngmDev40Win',
    qua40win: 'com.siemens.medical.solutions.usa.ngmQuality40Win',
    prod40win: 'com.siemens.medical.solutions.usa.ngmProd40Win',
    devSa: 'com.siemens.medical.solutions.usa.ngmDevSa',
    quaSa: 'com.siemens.medical.solutions.usa.ngmQualitySa',
    prodSa: 'com.siemens.medical.solutions.usa.ngmProdSa',
    pefsDev: 'com.siemens.medical.solutions.usa.DWHM',
    pefsQua: 'com.siemens.medical.solutions.usa.QWHM',
    pefsProd: 'com.siemens.medical.solutions.usa.PWHM',
    pwa: 'com.siemens.medical.solutions.usa.pacepwa',
};

const AppsP58 = [
    AppId.dev58,
    AppId.qua58,
    AppId.qua58_2,
    AppId.t58,
    AppId.sandboxp58,
    AppId.prod58,
    AppId.paceonline58,
    AppId.paceFormsFI,
    AppId.pwa,
];

const AppsCN = [AppId.devCn, AppId.devCn2, AppId.quaCn, AppId.quaCn2, AppId.prodCn, AppId.prodCn2];

const AppsAA = [
    AppId.devAa,
    AppId.devAa2,
    AppId.quaAa,
    AppId.quaAa2,
    AppId.prodAa,
    AppId.prodAa2,
    AppId.paceonlineAa,
    ...AppsCN,
];

const AppsUS = [AppId.dev40win, AppId.qua40win, AppId.prod40win];

const AppsWindows = [...AppsUS, AppId.pefsDev, AppId.pefsProd];

const AppsSA = [AppId.devSa, AppId.quaSa, AppId.prodSa];

const AppsIOS = [
    ...AppsCN,
    ...AppsAA,
    ...AppsSA,
    AppId.dev58,
    AppId.qua58,
    AppId.qua58_2,
    AppId.t58,
    AppId.sandboxp58,
    AppId.prod58,
];

const AppsPWA = [AppId.pwa];

@Injectable()
export class ClientManagerService implements ClientManagerInterface {
    public constructor(
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        @Inject(AppVersionInstanceToken) private readonly appVersion: AppVersion,
    ) {}

    // This is a hack.
    // We want to make the ClientManagerService to be able to perform more sophisticated checks like checking for AA and the country in one step.
    // The country comes from the userMasterService. As ClientManagerService is mostly used in a static way, we need to get the country value in,
    // so that the country can be stored and re-used for above mentioned checks.
    public userCountry: string;

    public getAppVersion(): string {
        return this.appVersion.version ?? `${this.appVersion.commitHash} (${this.appVersion.branchName})`;
    }

    public get version(): AppVersion {
        return this.appVersion;
    }

    /**
     * This function return true if the targetEnvironment is P58
     */
    public isNgmP58(): boolean {
        return AppsP58.indexOf(this.environment.appId) !== -1;
    }

    /**
     * This function return true if the targetEnvironment is NGM AA
     */
    public isNgmAA(): boolean {
        return AppsAA.indexOf(this.environment.appId) !== -1;
    }

    /**
     * This function return true if the targetEnvironment is NGM CHINA
     */
    public isNgmChina(): boolean {
        return this.userCountry?.toLowerCase() === 'cn';
    }

    /**
     * This function return true if the targetEnvironment is PACE FORMS FI for P58
     */
    public isPaceFormsFI(): boolean {
        return this.environment.appId === AppId.paceFormsFI;
    }

    /**
     * This function return true if the targetEnvironment is PACE FORMS FI for P58
     */
    public isPaceFormsForAuthors(): boolean {
        return this.environment.appId === AppId.paceFormsForAuthors;
    }

    /**
     * This function return true if the targetEnvironment is NGM AA
     */
    public isPaceUS(): boolean {
        return AppsUS.indexOf(this.environment.appId) !== -1;
    }

    /**
     * This function return true if the targetEnvironment is NGM SA
     */
    public isNgmSA(): boolean {
        return false;
    }

    public isNgmAAAustralia(): boolean {
        return this.isNgmAAAndUserCountry(ConfigConstants.configKeyAustralia);
    }

    public isNgmAANewZealand(): boolean {
        return this.isNgmAAAndUserCountry(ConfigConstants.configKeyNewZealand);
    }

    public isNgmAAChina(): boolean {
        return this.isNgmAAAndUserCountry(ConfigConstants.configKeyChina);
    }

    public isNgmAAANZ(): boolean {
        return this.isNgmAAAustralia() || this.isNgmAANewZealand();
    }

    public isNgmAAKorea(): boolean {
        return this.isNgmAAAndUserCountry(ConfigConstants.configKeyKorea);
    }

    public isNgmAAJapan(): boolean {
        return this.isNgmAAAndUserCountry(ConfigConstants.configKeyJapan);
    }

    public isNgmAAIndia(): boolean {
        return this.isNgmAAAndUserCountry(ConfigConstants.configKeyIndia);
    }

    private isNgmAAAndUserCountry(userCountry: string): boolean {
        if (this.isNgmAA() && !this.userCountry) {
            throw new Error('User country is not set for ClientManagerService.');
        }
        return this.isNgmAA() && this.userCountry === userCountry;
    }

    private isNgmP58AndUserCountry(userCountry: string): boolean {
        if (this.isNgmP58() && !this.userCountry) {
            throw new Error('User country is not set for ClientManagerService.');
        }
        return this.isNgmP58() && this.userCountry === userCountry;
    }

    public isNgmP58Switzerland(): boolean {
        return this.isNgmP58AndUserCountry(ConfigConstants.configKeySwitzerland);
    }

    public isNgmP58Netherlands(): boolean {
        return this.isNgmP58AndUserCountry(ConfigConstants.configKeyNetherlands);
    }

    public isNgmP58CHNL(): boolean {
        return this.isNgmP58Switzerland() || this.isNgmP58Netherlands();
    }

    public isNgmAP0(): boolean {
        return this.isNgmAA() || (this.isNgmSA() && !this.isNgmP58());
    }

    public isNgmP58OrSA(): boolean {
        return this.isNgmP58() || this.isNgmSA();
    }

    public isNgmP58Austria(): boolean {
        return this.isNgmP58AndUserCountry(ConfigConstants.configKeyAustria);
    }

    public isNgmP58Finland(): boolean {
        return this.isNgmP58AndUserCountry(ConfigConstants.configKeyFinland);
    }

    public isNgmP58Bulgaria(): boolean {
        return this.isNgmP58AndUserCountry(ConfigConstants.configKeyBulgaria);
    }

    public isWindows(): boolean {
        return AppsWindows.indexOf(this.environment.appId) !== -1;
    }

    public isIOS(): boolean {
        return AppsIOS.indexOf(this.environment.appId) !== -1;
    }

    public getDateFormat(short = false): string {
        if (this.isPaceFormsForEvo()) {
            return short
                ? ConfigConstants.formatPatternShortDatePaceFormsEvo
                : ConfigConstants.formatPatternDatePaceFormsEvo;
        } else if (this.isPaceUS()) {
            return short ? ConfigConstants.formatPatternShortDateUS : ConfigConstants.formatPatternDateUS;
        } else if (this.isNgmAAIndia()) {
            return short ? ConfigConstants.formatPatternShortDateIndia : ConfigConstants.formatPatternDateIndia;
        } else if (this.isNgmSA()) {
            return short ? ConfigConstants.formatPatternShortDateSaudi : ConfigConstants.formatPatternDateSaudi;
        } else {
            return short ? ConfigConstants.formatPatternShortDateGeneric : ConfigConstants.formatPatternDateGeneric;
        }
    }

    public getWeekdayFormat(): string {
        if (this.isPaceUS()) {
            return ConfigConstants.formatDayUs;
        }

        return ConfigConstants.formatDay;
    }

    public getTimeFormat(): string {
        let timeFormat;

        if (this.isNgmAAIndia() || this.isNgmSA() || this.isPaceUS()) {
            timeFormat = ConfigConstants.formatPatternTimeIn12Hours;
        } else {
            timeFormat = ConfigConstants.formatPatternTimeGeneric;
        }

        return timeFormat;
    }

    public getTimeWithSecondsFormat(): string {
        let timeFormat;

        if (this.isNgmAAIndia() || this.isNgmSA() || this.isPaceUS()) {
            timeFormat = ConfigConstants.formatPatternTimeIn12HoursWithSeconds;
        } else {
            timeFormat = ConfigConstants.formatPatternTimeWithSecondsGeneric;
        }

        return timeFormat;
    }

    public getDateTimeFormat(short = false): string {
        let dateTimeFormat;

        if (this.isPaceUS() && short) {
            dateTimeFormat = ConfigConstants.formatPatternShortDateTimeUS;
        } else if (this.isPaceUS()) {
            dateTimeFormat = ConfigConstants.formatPatternDateTimeUS;
        } else if (this.isNgmAAIndia() && short) {
            dateTimeFormat = ConfigConstants.formatPatternShortDateTimeIndia;
        } else if (this.isNgmAAIndia()) {
            dateTimeFormat = ConfigConstants.formatPatternDateTimeIndia;
        } else if (this.isNgmSA() && short) {
            dateTimeFormat = ConfigConstants.formatPatternShortDateTimeSaudi;
        } else if (this.isNgmSA()) {
            dateTimeFormat = ConfigConstants.formatPatternDateTimeSaudi;
        } else if (short) {
            dateTimeFormat = ConfigConstants.formatPatternShortDateTimeGeneric;
        } else {
            dateTimeFormat = ConfigConstants.formatPatternDateTimeGeneric;
        }
        return dateTimeFormat;
    }

    public getCurrentEnvironment(): string {
        const env = _.findKey<{ [key: string]: string }>(AppId, (value) => value === this.environment.appId);

        if (typeof env === 'undefined') {
            throw new Error(`Could not determine app environment!`);
        }

        return env;
    }

    public getCurrentAppId(): string {
        return this.environment.appId;
    }

    public isPaceFormsForEvo(): boolean {
        return this.environment.bundleIdentifierName === 'PACE forms for eVO';
    }

    public getBundleIdentifier(): string {
        return this.environment.bundleIdentifierName;
    }

    public isPacePWA(): boolean {
        return AppsPWA.indexOf(this.environment.appId) !== -1;
    }

    public isPacePWAStandalone(): boolean {
        const isStandalone = ['fullscreen', 'standalone', 'minimal-ui'].some(
            (displayMode) => window.matchMedia(`(display-mode: ${displayMode})`).matches,
        );
        return this.isPacePWA() && isStandalone;
    }

    public isPacePWABrowser(): boolean {
        const isStandalone = ['browser'].some(
            (displayMode) => window.matchMedia(`(display-mode: ${displayMode})`).matches,
        );
        return this.isPacePWA() && isStandalone;
    }

    public isPlatformIOS(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    }

    public isPlatformMacOS(): boolean {
        return /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.userAgent);
    }
}
